import { FC, Fragment } from "react";
import { useDropzone } from "react-dropzone";

import imgPlaceHolder from "../../../assets/galleryExport.svg";
import removeIcon from "../../../assets/removeImg.svg";
import {
  ICreateClubFormFields,
  ICreateClubWizardForm,
} from "../../../common/interfaces/clubs-wizard.interface";
import {
  mapSocialsToWizardFields,
  uploadImage,
  validateClubName,
} from "../../../utilities/helpers";
import SocialNetworks from "../../UserSettingsForm/SocialNetworks/SocialNetworks";
import WizardField from "../../WizardField/WizardField";
import ApplicationStatusBar from "../ApplicationStatusBar/ApplicationStatusBar";

import ClubTags from "./ClubTags/ClubTags";
import "./ClubBasicInfo.scss";
import {
  EMPTY_STRING,
  MAX_IMAGE_SIZE,
} from "../../../common/constants/common.constants";
import {
  IUpdateClubFormFields,
  IUpdateClubFormValues,
} from "../../../common/interfaces/clubs.interface";

const ClubBasicInfo: FC<{
  formFields: ICreateClubFormFields | IUpdateClubFormFields;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  values: ICreateClubWizardForm | IUpdateClubFormValues;
  setSubmitting: (isSubmitting: boolean) => void;
  isClubUpdate?: boolean;
  shouldNotUpdateApplicationStatus?: boolean;
}> = ({
  formFields,
  setFieldValue,
  values,
  setSubmitting,
  isClubUpdate,
  shouldNotUpdateApplicationStatus,
}) => {
  const reader = new FileReader();

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: "image/png, image/jpg, image/jpeg, image/gif",
    maxSize: MAX_IMAGE_SIZE,
    onDrop: (acceptedFiles) =>
      uploadImage(
        acceptedFiles,
        reader,
        "clubImage",
        setFieldValue,
        false,
        isClubUpdate
      ),
  });

  const validateUniqueName = async () => {
    if (values.clubName !== EMPTY_STRING && !isClubUpdate) {
      setSubmitting(true);
      const isValid = await validateClubName(values.clubName);
      if (!isValid) {
        setFieldValue("isClubNameTaken", true);
      } else {
        setFieldValue("isClubNameTaken", false);
      }
      setSubmitting(false);
    }
  };

  const removeImage = async (e) => {
    e.stopPropagation();
    setFieldValue("clubImage", EMPTY_STRING);
    isClubUpdate && setFieldValue("removeImage", true);
  };

  const socialNetworks = mapSocialsToWizardFields(formFields);

  return (
    <div className="club-basic-info">
      <div className="club-basic-info__upload-image">
        <div className="club-basic-info__image_container">
          <div className="club-basic-info__image" onClick={open}>
            {values.clubImage ? (
              <Fragment>
                <img
                  className="club-basic-info__avatar"
                  src={values.clubImage}
                  alt="Club avatar"
                />
                <img
                  className="club-basic-info__removeIcon"
                  src={removeIcon}
                  alt="Remove avatar"
                  onClick={removeImage}
                />
              </Fragment>
            ) : (
              <img src={imgPlaceHolder} alt="Club avatar" />
            )}
          </div>
        </div>
        <div {...getRootProps()} className="club-basic-info__upload-actions">
          <input {...getInputProps()} />
          <button type="button" onClick={open}>
            Upload image
          </button>
          <p>
            Upload your Club's avatar for better recognition. <br />
            Formats: png, jpg, gif. Max size: 2 MB
          </p>
        </div>
      </div>
      <WizardField
        type="text"
        name={formFields.clubName.name}
        placeholder="Enter a unique club name here"
        label={formFields.clubName.label}
        validateField={validateUniqueName}
        required
        disabled={isClubUpdate}
      />
      <WizardField
        type="text"
        name={formFields.clubEmail.name}
        placeholder="Enter email for communication here"
        label={formFields.clubEmail.label}
        required
      />
      {!shouldNotUpdateApplicationStatus && (
        <ApplicationStatusBar
          clubApplicationStatus={formFields.clubApplicationStatus}
        />
      )}
      <WizardField
        type="textarea"
        name={formFields.clubDescription.name}
        placeholder="What's your club all about and who is it for?"
        label={formFields.clubDescription.label}
        required
      />
      <p className="club-basic-info__char-counter">
        {values.clubDescription.length}/500
      </p>

      <ClubTags />
      <SocialNetworks
        socialNetworks={socialNetworks}
        createClubForm={true}
        canAddLater={true}
      />
    </div>
  );
};

export default ClubBasicInfo;
